// ---------------------------------------------------------------------------
// «إنشاء طلب التوصيل» - the moment a delivery leaves the till.
//
// The Current Order plus a positive fee IS the delivery; this dialog is only
// the confirmation of what dispatching means:
//
//   * the figures on screen FREEZE - the receipt printing now and the account
//     collected later both read exactly them;
//   * the customer receipt prints immediately and goes with the driver;
//   * preparation goes to the benches once, here - unless the cashier ticks
//     «بدون تحضير», the same order-level exception a sale carries;
//   * NO money moves. The delivery enters the Delivery View as «بانتظار
//     الحساب» and the drawer is touched only when the driver comes back.
//
// The skip checkbox starts unchecked on every open: SKIP is an exception,
// never a preference, exactly as in the payment dialog.
// ---------------------------------------------------------------------------

import { useEffect, useState } from 'react';
import { AlertCircle, Printer, Truck } from 'lucide-react';
import clsx from 'clsx';
import { Modal } from '../common/Modal';
import { useI18n } from '../../i18n/I18nProvider';
import { formatLbp, formatUsd } from '../../utils/format';

interface DeliveryDispatchDialogProps {
  open: boolean;
  onClose: () => void;
  itemCount: number;
  /// The three lira figures the Current Order shows, passed through - never
  /// recomputed here. Null when no rate is set, in which case dispatch is
  /// blocked before this dialog ever opens.
  itemsSubtotalLbp: string | null;
  feeLbp: string;
  totalLbp: string | null;
  totalUsd: string;
  /// Money-amount lines and the weight each became, shown rather than
  /// converted quietly - the butcher's ticket carries a weight.
  convertedLines: Array<{ id: string; name: string; amount: string; quantity: string }>;
  /// Whether anything in the cart routes to a preparation bench - the only
  /// case where «بدون تحضير» means anything.
  hasPreparationItems: boolean;
  submitting: boolean;
  errorMessage: string | null;
  onConfirm: (skipPreparation: boolean) => void;
}

export function DeliveryDispatchDialog({
  open,
  onClose,
  itemCount,
  itemsSubtotalLbp,
  feeLbp,
  totalLbp,
  totalUsd,
  convertedLines,
  hasPreparationItems,
  submitting,
  errorMessage,
  onConfirm,
}: DeliveryDispatchDialogProps) {
  const { t, format } = useI18n();

  /// The order-level exception, reset on every open so one delivery's skip
  /// can never leak into the next.
  const [skipPreparation, setSkipPreparation] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSkipPreparation(false);
  }, [open]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t.delivery.dispatchTitle}
      size="md"
      guardUnsaved
      footer={
        <>
          <button type="button" className="btn-secondary" onClick={onClose} disabled={submitting}>
            {t.common.cancel}
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={() => onConfirm(skipPreparation)}
            disabled={submitting}
          >
            <Truck className="h-4 w-4" aria-hidden="true" />
            {submitting ? t.delivery.dispatching : t.delivery.dispatchConfirm}
          </button>
        </>
      }
    >
      {/* --- what is leaving ------------------------------------------- */}
      <div className="rounded-card border border-line bg-surface-soft p-4">
        <div className="flex items-baseline justify-between text-sm">
          <span className="text-ink-muted">
            {itemCount === 1 ? t.pos.oneItem : format(t.pos.items, { count: itemCount })}
          </span>
        </div>

        <div className="mt-2 space-y-1.5 text-sm">
          <div className="flex items-baseline justify-between">
            <span className="text-ink-muted">{t.delivery.itemsSubtotal}</span>
            <span className="numeric text-ink">{itemsSubtotalLbp ? formatLbp(itemsSubtotalLbp) : '—'}</span>
          </div>
          <div className="flex items-baseline justify-between">
            <span className="text-ink-muted">{t.delivery.fee}</span>
            <span className="numeric text-ink">{formatLbp(feeLbp)}</span>
          </div>
          <div className="flex items-baseline justify-between border-t border-line pt-2">
            <span className="font-semibold text-ink">{t.delivery.finalTotal}</span>
            <span className="numeric flex flex-col items-end leading-tight">
              <span className="text-xl font-bold text-butcher-green">
                {totalLbp ? formatLbp(totalLbp) : formatUsd(totalUsd)}
              </span>
              {totalLbp ? (
                <span className="text-sm font-semibold text-ink-muted">{formatUsd(totalUsd)}</span>
              ) : null}
            </span>
          </div>
        </div>
      </div>

      {/* What a money amount becomes on the butcher's ticket - shown, not
          done quietly. */}
      {convertedLines.length > 0 ? (
        <div className="mt-3 rounded-card border border-antique-gold/40 bg-antique-gold/10 p-3">
          {convertedLines.map((line) => (
            <p key={line.id} className="break-words text-xs text-antique-gold-deep">
              {t.delivery.amountConverted
                .replace('{name}', line.name)
                .replace('{amount}', line.amount)
                .replace('{quantity}', line.quantity)}
            </p>
          ))}
        </div>
      ) : null}

      {/* What confirming does: the paper goes with the driver, the money
          waits for him to come back. Said before the button, because a
          cashier who expects a payment dialog needs to know none is coming. */}
      <p className="mt-3 flex items-start gap-2 rounded-card border border-line bg-surface-soft p-3 text-xs text-ink">
        <Printer className="mt-0.5 h-4 w-4 shrink-0 text-butcher-green" aria-hidden="true" />
        {t.delivery.dispatchHint}
      </p>

      {/* «بدون تحضير» - only when the cart would actually reach a bench. */}
      {hasPreparationItems ? (
        <label
          className={clsx(
            'mt-3 flex cursor-pointer items-start gap-2.5 rounded-card border-2 p-3 transition-colors duration-150 motion-reduce:transition-none',
            skipPreparation
              ? 'border-antique-gold bg-antique-gold/10'
              : 'border-line bg-surface-soft hover:border-line-strong',
          )}
        >
          <input
            type="checkbox"
            checked={skipPreparation}
            onChange={(event) => setSkipPreparation(event.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-line-strong text-butcher-green focus:ring-antique-gold"
          />
          <span className="min-w-0">
            <span className="block text-sm font-semibold text-ink">{t.payment.skipPreparation}</span>
            {skipPreparation ? (
              <span
                className="mt-0.5 flex items-start gap-1 text-xs font-medium text-antique-gold-deep"
                role="status"
              >
                <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                {t.payment.skipPreparationWarning}
              </span>
            ) : (
              <span className="mt-0.5 block text-xs text-ink-muted">
                {t.payment.skipPreparationHint}
              </span>
            )}
          </span>
        </label>
      ) : null}

      {errorMessage ? (
        <div
          className="mt-4 flex gap-2.5 rounded-control border border-burgundy-red/30 bg-burgundy-red/10 p-3 text-sm"
          role="alert"
        >
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-burgundy-red" aria-hidden="true" />
          <div>
            <p className="font-semibold text-burgundy-red">{t.delivery.dispatchFailed}</p>
            <p className="mt-0.5 text-ink">{errorMessage}</p>
          </div>
        </div>
      ) : null}
    </Modal>
  );
}
